import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { User, TrendingUp, Calendar, Settings, Award, Search, X, Info, Package, AlertTriangle, Clock, BarChart3, Users, MapPin, Upload, Stethoscope, ClipboardList, LogOut } from 'lucide-react';
import HealthProfile from '@/components/onboarding/HealthProfile';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { safeArray } from '@/components/utils/safeArray';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import EscanEatScore from '@/components/products/ScanEatScore';
import AdditiveAlerts from '@/components/additives/AdditiveAlerts';
import AdditiveDetailModal from '@/components/additives/AdditiveDetailModal';
import CommunityRecommendations from '@/components/social/CommunityRecommendations';
import InviteButton from '@/components/social/InviteButton';
import LocationSettings from '@/components/profile/LocationSettings';
import PrivacyNotice from '@/components/privacy/PrivacyNotice';
import ProductDetail from '@/components/products/ProductDetail';
import ProfileEditor from '@/components/profile/ProfileEditor';
import ProfessionalBadge from '@/components/profile/ProfessionalBadge';
import BecomeProfessional from '@/components/profile/BecomeProfessional';
import SelectProfessional from '@/components/profile/SelectProfessional';

export default function Profile() {
    const [user, setUser] = useState(null);
    const [showSettings, setShowSettings] = useState(false);
    const [showLocationSettings, setShowLocationSettings] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedAdditive, setSelectedAdditive] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showProfileEditor, setShowProfileEditor] = useState(false);
    const [showBecomeProfessional, setShowBecomeProfessional] = useState(false);
    const [showSelectProfessional, setShowSelectProfessional] = useState(false);
    const [showHealthOnboarding, setShowHealthOnboarding] = useState(false);
    const queryClient = useQueryClient();

  useEffect(() => {
    const loadUser = async () => {
      try {
        const currentUser = await base44.auth.me();
        setUser(currentUser);
      } catch (error) {
        console.error('Error loading user:', error);
      }
    };
    loadUser();
  }, []);

  const { data: products = [] } = useQuery({
      queryKey: ['products', user?.id],
      queryFn: async () => {
          const data = await base44.entities.Product.filter({ created_by: user?.email }, '-created_date');
          return safeArray(data);
      },
      enabled: !!user?.id,
  });

  const { data: additives = [] } = useQuery({
      queryKey: ['additives', user?.id],
      queryFn: async () => {
          // Get user's products first
          const userProducts = safeArray(await base44.entities.Product.filter({ created_by: user?.email }));
          const productIds = userProducts.map(p => p.id);

          // Get all additives
          const allAdditives = safeArray(await base44.entities.Additive.list('-created_date'));

          // Filter to only include additives found in user's products
          return allAdditives.filter(additive => 
              safeArray(additive.product_ids).some(pid => productIds.includes(pid))
          );
      },
      enabled: !!user?.email,
  });

  const { data: memberships = [] } = useQuery({
    queryKey: ['my-memberships', user?.id],
    queryFn: async () => {
        const data = await base44.entities.GroupMembership.filter({ user_id: user?.id });
        return safeArray(data);
    },
    enabled: !!user?.id,
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
        const data = await base44.entities.Group.list();
        return safeArray(data);
    },
  });

  // Calculate health score based on scanned products
  const calculateHealthScore = () => {
    const productsArray = safeArray(products);
    if (productsArray.length === 0) return null;
    
    const scores = productsArray
      .filter(p => p.scaneat_score !== undefined)
      .map(p => p.scaneat_score);
    
    if (scores.length === 0) return null;
    
    const avg = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    return Math.round(avg);
  };

  const getTrafficLight = (score) => {
    if (score >= 70) return 'green';
    if (score >= 40) return 'yellow';
    return 'red';
  };

  const healthScore = calculateHealthScore();
  const trafficLight = healthScore ? getTrafficLight(healthScore) : 'yellow';

  // Group products by month
  const productsByMonth = safeArray(products).reduce((acc, product) => {
    const month = format(new Date(product.created_date), 'MMMM yyyy', { locale: es });
    if (!acc[month]) acc[month] = [];
    acc[month].push(product);
    return acc;
  }, {});

  const conditions = [
    ...safeArray(user?.health_conditions).filter(c => c !== 'ninguna'),
    ...safeArray(user?.custom_health_conditions)
  ];

  const filteredAdditives = safeArray(additives).filter(additive => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      additive.code?.toLowerCase().includes(query) ||
      additive.name?.toLowerCase().includes(query) ||
      additive.description?.toLowerCase().includes(query)
    );
  });

  const getAdditiveProducts = (additive) => {
    const productIds = safeArray(additive.product_ids);
    if (productIds.length === 0) return [];
    return safeArray(products).filter(p => productIds.includes(p.id));
  };

  const getRiskColor = (level) => {
    switch(level) {
      case 'bajo': return { bg: 'bg-emerald-50', text: 'text-emerald-700', badge: 'bg-emerald-100' };
      case 'moderado': return { bg: 'bg-amber-50', text: 'text-amber-700', badge: 'bg-amber-100' };
      case 'alto': return { bg: 'bg-red-50', text: 'text-red-700', badge: 'bg-red-100' };
      default: return { bg: 'bg-gray-50', text: 'text-gray-700', badge: 'bg-gray-100' };
    }
  };

  // Calculate additive history
  const getAdditiveHistory = () => {
    const additiveCount = {};
    const additiveByMonth = {};
    
    safeArray(products).forEach(product => {
      const productAdditives = safeArray(product.additives);
      if (productAdditives.length > 0) {
        const month = format(new Date(product.created_date), 'MMM yyyy', { locale: es });
        
        productAdditives.forEach(additive => {
          const key = additive.code || additive.name;
          
          // Count total
          if (!additiveCount[key]) {
            additiveCount[key] = {
              ...additive,
              count: 0,
              products: []
            };
          }
          additiveCount[key].count++;
          additiveCount[key].products.push(product.id);
          
          // Count by month
          if (!additiveByMonth[month]) {
            additiveByMonth[month] = {};
          }
          if (!additiveByMonth[month][key]) {
            additiveByMonth[month][key] = 0;
          }
          additiveByMonth[month][key]++;
        });
      }
    });
    
    const sortedAdditives = Object.values(additiveCount)
      .sort((a, b) => b.count - a.count);
    
    return { additiveCount: sortedAdditives, additiveByMonth };
  };

  const { additiveCount, additiveByMonth } = getAdditiveHistory();

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-gray-50 pb-24 overflow-x-hidden">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40 safe-header">
        <div className="max-w-2xl mx-auto px-3 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="font-bold text-gray-900 text-base sm:text-lg leading-tight">Mi Perfil</h1>
                <p className="text-[10px] sm:text-xs text-gray-400 hidden xs:block">Tu salud alimentaria</p>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <InviteButton variant="compact" />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowProfileEditor(true)}
                className="rounded-xl px-2 h-8"
              >
                <User className="w-3.5 h-3.5" />
                <span className="hidden sm:inline ml-1 text-xs">Editar</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowHealthOnboarding(true)}
                className="rounded-xl px-2 h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                title="Configurar perfil de salud"
              >
                <ClipboardList className="w-3.5 h-3.5" />
                <span className="hidden sm:inline ml-1 text-xs">Salud</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => base44.auth.logout()}
                className="rounded-xl w-8 h-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                title="Cerrar sesión"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Privacy Notice */}
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
        >
            <PrivacyNotice />
        </motion.div>

        {/* User Info */}
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-gray-100 mb-4 sm:mb-6"
        >
          <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="relative group">
              {user?.profile_picture ? (
                <img 
                  src={user.profile_picture} 
                  alt={user.full_name}
                  className="w-16 h-16 rounded-2xl object-cover shadow-lg"
                />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                  {user?.full_name?.[0]?.toUpperCase()}
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                id="profile-picture-upload"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    try {
                      const { file_url } = await base44.integrations.Core.UploadFile({ file });
                      await base44.auth.updateMe({ profile_picture: file_url });
                      setUser({ ...user, profile_picture: file_url });
                    } catch (error) {
                      console.error('Error uploading profile picture:', error);
                    }
                  }
                }}
              />
              <label 
                htmlFor="profile-picture-upload"
                className="absolute inset-0 bg-black/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center"
              >
                <Upload className="w-6 h-6 text-white" />
              </label>
              {user?.profile_picture && (
                <button
                  onClick={async () => {
                    await base44.auth.updateMe({ profile_picture: null });
                    setUser({ ...user, profile_picture: null });
                  }}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-xl font-bold text-gray-900">{user?.full_name}</h2>
                <ProfessionalBadge user={user} size="md" showLabel={false} />
              </div>
              <p className="text-sm text-gray-500">{user?.email}</p>
              {user?.is_professional && user?.professional_type && (
                <p className="text-sm text-blue-600 mt-1">
                  {user.professional_type === 'nutricionista' ? 'Nutricionista' :
                   user.professional_type === 'medico' ? 'Médico' :
                   user.professional_type === 'enfermero' ? 'Enfermero' :
                   user.professional_type === 'farmaceutico' ? 'Farmacéutico' :
                   'Profesional de salud'}
                  {user.professional_specialty && ` - ${user.professional_specialty}`}
                </p>
              )}
            </div>
          </div>

          {user?.is_professional && user?.professional_bio && (
            <div className="mb-6 p-4 bg-blue-50 rounded-xl">
              <p className="text-sm text-gray-700">{user.professional_bio}</p>
            </div>
          )}

          {user?.is_professional && user?.professional_license && (
            <div className="mb-6 flex items-center gap-2 text-sm">
              <span className="text-gray-500">Matrícula:</span>
              <span className="font-medium text-gray-900">{user.professional_license}</span>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-4">
            {!user?.is_professional && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBecomeProfessional(true)}
                className="border-blue-200 text-blue-600 hover:bg-blue-50 text-xs"
              >
                Ser profesional
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSelectProfessional(true)}
              className="border-green-200 text-green-600 hover:bg-green-50 text-xs"
            >
              <Stethoscope className="w-3.5 h-3.5 mr-1" />
              Mi Profesional
            </Button>
          </div>

          {user?.is_professional && !user?.professional_verified && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700">
                ⏳ Tu perfil profesional está pendiente de verificación administrativa
              </p>
            </div>
          )}

          {safeArray(conditions).length > 0 && (
            <div className="pt-4 border-t border-gray-100">
              <p className="text-sm font-medium text-gray-500 mb-3">Condiciones de salud</p>
              <div className="flex flex-wrap gap-2">
                {safeArray(conditions).map((condition, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-sm font-medium"
                  >
                    {condition}
                  </span>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        {/* Health Score */}
        {healthScore !== null && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6"
          >
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Índice de Salud Alimentaria</h3>
              </div>

              <EscanEatScore score={healthScore} trafficLight={trafficLight} />

              <p className="text-sm text-gray-600 mt-4">
                Basado en el análisis de {safeArray(products).length} producto{safeArray(products).length !== 1 ? 's' : ''} escaneado{safeArray(products).length !== 1 ? 's' : ''}
              </p>
            </div>
          </motion.div>
        )}

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 gap-4 mb-6"
        >
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-emerald-600" />
              <p className="text-sm text-gray-500">Productos</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{safeArray(products).length}</p>
          </div>

          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <p className="text-sm text-gray-500">Este mes</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {productsByMonth[Object.keys(productsByMonth)[0]]?.length || 0}
            </p>
          </div>
        </motion.div>

        {/* My Groups */}
        {safeArray(memberships).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-gray-900">Mis Grupos</h3>
            </div>
            <div className="grid gap-3">
              {safeArray(memberships).map((membership) => {
                const group = safeArray(groups).find(g => g.id === membership.group_id);
                if (!group) return null;

                return (
                  <Link key={membership.id} to={createPageUrl('Community')}>
                    <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{group.name}</h4>
                          {group.description && (
                            <p className="text-sm text-gray-600 mt-1 line-clamp-1">{group.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Users className="w-4 h-4" />
                          <span>{group.member_count || 0}</span>
                        </div>
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Community Recommendations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <CommunityRecommendations userConditions={conditions} />
        </motion.div>

        {/* Additive Alerts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <AdditiveAlerts
            userAdditiveAlerts={user?.additive_alerts || []}
            userVitaminAlerts={user?.vitamin_alerts || []}
            userIngredientAlerts={user?.ingredient_alerts || []}
            onSave={async (alerts) => {
              await base44.auth.updateMe(alerts);
              setUser({ ...user, ...alerts });
            }}
          />
        </motion.div>

        {/* Health Warnings Summary */}
        {safeArray(additives).length > 0 && safeArray(conditions).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mb-6"
          >
            <Card className="p-6 border-2 border-amber-200 bg-amber-50">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-2">Advertencias Personalizadas</h3>
                  <p className="text-sm text-gray-700 mb-3">
                    Basado en tus condiciones de salud ({safeArray(conditions).join(', ')}), estos aditivos requieren tu atención:
                  </p>
                  <div className="space-y-2">
                    {safeArray(additives).filter(a => a.risk_level === 'alto').slice(0, 3).map((additive) => (
                      <div key={additive.id} className="flex items-start gap-2 p-3 bg-white rounded-lg">
                        <div className="w-10 h-10 rounded-lg bg-red-100 text-red-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {additive.code}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm text-gray-900">{additive.name}</p>
                          <p className="text-xs text-gray-600 mt-1">{additive.health_effects || additive.description}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedAdditive(additive)}
                          className="text-xs"
                        >
                          Ver más
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Additive History */}
        {safeArray(additiveCount).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-gray-900">Historial de Aditivos</h3>
            </div>

            <Card className="p-6 mb-4">
              <div className="flex items-center gap-3 mb-4">
                <BarChart3 className="w-5 h-5 text-purple-600" />
                <h4 className="font-semibold text-gray-900">Aditivos más frecuentes en tus productos</h4>
              </div>
              
              <div className="space-y-3">
                {safeArray(additiveCount).slice(0, 5).map((additive, index) => {
                  const colors = getRiskColor(additive.risk_level);
                  const percentage = (additive.count / safeArray(products).length) * 100;
                  
                  return (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold ${colors.bg} ${colors.text}`}>
                            {additive.code}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900 truncate">{additive.name}</p>
                            <p className="text-xs text-gray-500">
                              En {additive.count} producto{additive.count !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            const fullAdditive = safeArray(additives).find(a => a.code === additive.code);
                            if (fullAdditive) setSelectedAdditive(fullAdditive);
                          }}
                          className="text-xs"
                        >
                          Ver
                        </Button>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            additive.risk_level === 'alto' ? 'bg-red-500' :
                            additive.risk_level === 'moderado' ? 'bg-amber-500' :
                            'bg-emerald-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {Object.keys(additiveByMonth).length > 1 && (
              <Card className="p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Tendencia mensual</h4>
                <div className="space-y-4">
                  {Object.entries(additiveByMonth).slice(0, 3).map(([month, additives]) => {
                    const totalAdditives = Object.values(additives).reduce((sum, count) => sum + count, 0);
                    const highRisk = Object.entries(additives).filter(([key]) => {
                      const additive = safeArray(additiveCount).find(a => (a.code || a.name) === key);
                      return additive?.risk_level === 'alto';
                    }).length;
                    
                    return (
                      <div key={month} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900 capitalize">{month}</p>
                          <p className="text-xs text-gray-500">
                            {totalAdditives} aditivo{totalAdditives !== 1 ? 's' : ''} detectado{totalAdditives !== 1 ? 's' : ''}
                          </p>
                        </div>
                        {highRisk > 0 && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-red-100 rounded-full">
                            <AlertTriangle className="w-3 h-3 text-red-600" />
                            <span className="text-xs font-medium text-red-700">{highRisk} alto riesgo</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
          </motion.div>
        )}

        {/* Additives Section */}
        {safeArray(additives).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="mb-6"
          >
            <h3 className="font-semibold text-gray-900 mb-4">Base de Datos de Aditivos</h3>
            
            <div className="bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl p-5 mb-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90 mb-1">Total de aditivos detectados</p>
                  <p className="text-3xl font-bold">{safeArray(additives).length}</p>
                </div>
                <Info className="w-12 h-12 opacity-20" />
              </div>
            </div>

            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por código (ej: E102) o nombre..."
                className="pl-10 h-12 rounded-xl border-gray-200 bg-white"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              )}
            </div>

            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="space-y-3">
                {safeArray(filteredAdditives).map((additive) => {
                  const colors = getRiskColor(additive.risk_level);
                  return (
                    <div
                      key={additive.id}
                      onClick={() => setSelectedAdditive(additive)}
                      className="flex items-start gap-3 pb-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors cursor-pointer"
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xs font-bold flex-shrink-0 ${colors.bg} ${colors.text}`}>
                        {additive.code}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900">{additive.name}</p>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{additive.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors.badge} ${colors.text}`}>
                            Riesgo {additive.risk_level}
                          </span>
                          <span className="text-xs text-gray-400">
                            En {safeArray(additive.product_ids).length} producto{safeArray(additive.product_ids).length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}

        {/* History by Month */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h3 className="font-semibold text-gray-900 mb-4">Historial de escaneos</h3>
          
          {safeArray(products).length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-100">
              <p className="text-gray-400">Aún no has escaneado productos</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(productsByMonth).map(([month, monthProducts]) => (
                <div key={month} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                  <h4 className="font-semibold text-gray-700 mb-4 capitalize">{month}</h4>
                  <div className="space-y-3">
                    {safeArray(monthProducts).map((product) => (
                      <div 
                        key={product.id} 
                        onClick={() => setSelectedProduct(product)}
                        className="flex items-center gap-3 pb-3 border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors"
                      >
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-gray-100" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{product.name}</p>
                          <p className="text-xs text-gray-400">
                            {format(new Date(product.created_date), "d MMM", { locale: es })}
                          </p>
                        </div>
                        {product.scaneat_score !== undefined && (
                          <div className={`
                            w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm
                            ${product.traffic_light === 'green' ? 'bg-emerald-50 text-emerald-700' :
                              product.traffic_light === 'yellow' ? 'bg-amber-50 text-amber-700' :
                              'bg-red-50 text-red-700'}
                          `}>
                            {product.scaneat_score}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </main>

      {/* Additive Detail Modal */}
      {selectedAdditive && (
        <AdditiveDetailModal
          additive={selectedAdditive}
          products={getAdditiveProducts(selectedAdditive)}
          onClose={() => setSelectedAdditive(null)}
        />
      )}

      {showLocationSettings && (
        <LocationSettings
          user={user}
          onSave={async (data) => {
            await base44.auth.updateMe(data);
            setUser({ ...user, ...data });
            setShowLocationSettings(false);
          }}
          onClose={() => setShowLocationSettings(false)}
        />
      )}

      {selectedProduct && (
        <ProductDetail
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onDelete={async (id) => {
            await base44.entities.Product.delete(id);
            queryClient.invalidateQueries({ queryKey: ['products'] });
            setSelectedProduct(null);
          }}
          onEdit={async (id, data) => {
            await base44.entities.Product.update(id, data);
            queryClient.invalidateQueries({ queryKey: ['products'] });
          }}
        />
      )}

      {showProfileEditor && (
        <ProfileEditor
          user={user}
          onSave={async (data) => {
            await base44.auth.updateMe(data);
            setUser({ ...user, ...data });
            setShowProfileEditor(false);
          }}
          onClose={() => setShowProfileEditor(false)}
        />
      )}

      {showBecomeProfessional && (
        <BecomeProfessional
          user={user}
          onSave={async (data) => {
            await base44.auth.updateMe(data);
            setUser({ ...user, ...data });
            setShowBecomeProfessional(false);
          }}
          onClose={() => setShowBecomeProfessional(false)}
        />
      )}

      {showSelectProfessional && (
        <SelectProfessional
          user={user}
          onClose={() => setShowSelectProfessional(false)}
        />
      )}

      <AnimatePresence>
        {showHealthOnboarding && (
          <motion.div
            key="health-onboarding-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-white overflow-y-auto"
          >
            <HealthProfile
              initialData={user}
              onComplete={async (profileData) => {
                await base44.auth.updateMe({ ...profileData, onboarding_completed: true });
                setUser({ ...user, ...profileData, onboarding_completed: true });
                setShowHealthOnboarding(false);
              }}
              onSkip={() => setShowHealthOnboarding(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>
      </div>
      );
      }